import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import * as rxjs from 'rxjs'

import type * as models from '../models'
import type { Plugin } from '../plugin'

/**
 * Retrieves desired entities, writes them into an Excel
 * {@link models.Workbook}, and then downloads that {@link models.Workbook}
 */
export const download = store.effects.create(
  <TTypeParams extends ec.models.TypeParams.Constraint.Default>(
    feature: Plugin<TTypeParams>['feature'],
    plugin: Plugin<TTypeParams>,
    getDownload: models.Download.Producer<TTypeParams>,
  ) =>
    feature.actions.download.request.observe().pipe(
      // NOTE: mergeMap ensures every request is handled in parallel
      rxjs.mergeMap(action => {
        const { entities$, ids$, request$, mix$ } = action.data
        const entitiesToDownload$ =
          entities$?.() ??
          ids$?.().pipe(
            // Uses list query with ids
            rxjs.exhaustMap(
              ids =>
                plugin.collection.plugins.dataOperationPlugin.feature.actions.list
                  .from(action)
                  .execute({ ids }).asObservable,
            ),
            // Unpacks the response to just get the list
            rxjs.map(({ entities }) => entities),
          ) ??
          request$?.().pipe(
            // Uses list query with request
            rxjs.exhaustMap(
              request =>
                plugin.collection.plugins.dataOperationPlugin.feature.actions.list
                  .from(action)
                  .execute(request).asObservable,
            ),
            // Unpacks the response to just get the list
            rxjs.map(({ entities }) => entities),
          ) ??
          mix$?.().pipe(
            // Uses list query with request
            rxjs.exhaustMap(({ entities, ids, request }) => {
              if (entities) {
                // Returns the entities as is
                return rxjs.of(entities)
              }
              if (ids ?? request) {
                // Makes a list request
                return plugin.collection.plugins.dataOperationPlugin.feature.actions.list
                  .from(action)
                  .execute(ids ? { ids } : (request ?? {}))
                  .asObservable.pipe(
                    // Unpacks the response to just get the list
                    rxjs.map(response => response.entities),
                  )
              }
              // Returns empty list
              return rxjs.of([])
            }),
          ) ??
          rxjs.of([])
        // Downloads the entities
        return entitiesToDownload$.pipe(
          rxjsUtils.latestFrom(
            entities =>
              ({
                attributes:
                  plugin.collection.plugins.attributePlugin.feature.selectors
                    .attributes.asObservable,
                entities: rxjs.of(entities),
              }) satisfies EachPropertyAsObservable<
                models.Download.Producer.Params<TTypeParams>
              >,
          ),
          // NOTE: exhaustMap ensures that the request finishes before getting
          // interrupted. Although, it shouldn't matter since we only take 1
          // emission anyway
          rxjs.exhaustMap(params => {
            // Gets the download
            const _download = getDownload(params)
            return rxjs.isObservable(_download) ? _download : rxjs.of(_download)
          }),
          // Saves the workbook
          rxjs.exhaustMap(async _download => {
            await _download.workbook.saveAs(_download.filename)
            return feature.actions.download.success.from(action).create({
              request: action.data,
              response: _download,
            })
          }),
          rxjs.take(1),
          rxjs.catchError(error =>
            rxjs.of(
              feature.actions.download.error.from(action).create({
                error,
                request: action.data,
              }),
            ),
          ),
        )
      }),
    ),
)

type EachPropertyAsObservable<T> = {
  [K in keyof T]: rxjs.Observable<T[K]>
}
